import React, { createContext, useContext, useEffect, useState } from "react";
import { zugriffApi } from "../services/api/zugriff";
import { BereichKey, MeineRechte } from "../types/zugriff";
import { darfBearbeiten, darfSehen } from "../utils/zugriff";

interface ProjektRechteContextType {
  /** Bereichsschlüssel -> Modus. Fehlende Bereiche sind gesperrt. */
  rechte: MeineRechte;
  /** Ob der Nutzer zum Mandanten des Projekts gehört. */
  istIntern: boolean;
  /** Solange false, ist `rechte` noch leer – Navigation darf noch nichts ausblenden. */
  geladen: boolean;
  darfSehen: (bereich?: BereichKey) => boolean;
  darfBearbeiten: (bereich?: BereichKey) => boolean;
}

const ProjektRechteContext = createContext<
  ProjektRechteContextType | undefined
>(undefined);

/**
 * Lädt die effektiven Rechte für das aktuelle Projekt und stellt sie bereit.
 *
 * Sie kommen aus der Datenbank, nicht aus dem Auth0-Token: Ein externer Nutzer trägt dort
 * keine fachlichen Permissions, und ein Entzug soll sofort wirken statt erst beim nächsten
 * Token. Deshalb wird beim Projektwechsel neu geladen.
 */
export const ProjektRechteProvider: React.FC<{
  projectId: string;
  children: React.ReactNode;
}> = ({ projectId, children }) => {
  const [rechte, setRechte] = useState<MeineRechte>({});
  const [istIntern, setIstIntern] = useState(true);
  const [geladen, setGeladen] = useState(false);

  useEffect(() => {
    let abgebrochen = false;
    setGeladen(false);

    zugriffApi
      .getMeineRechte(projectId)
      .then((antwort) => {
        if (abgebrochen) return;
        setRechte(antwort.rechte);
        setIstIntern(antwort.ist_intern);
      })
      .catch(() => {
        // Ohne Rechte bleibt alles gesperrt – das Backend weist jeden Zugriff ohnehin ab.
        if (!abgebrochen) {
          setRechte({});
          setIstIntern(false);
        }
      })
      .finally(() => {
        if (!abgebrochen) setGeladen(true);
      });

    return () => {
      abgebrochen = true;
    };
  }, [projectId]);

  return (
    <ProjektRechteContext.Provider
      value={{
        rechte,
        istIntern,
        geladen,
        darfSehen: (bereich) => darfSehen(rechte, bereich),
        darfBearbeiten: (bereich) => darfBearbeiten(rechte, bereich),
      }}
    >
      {children}
    </ProjektRechteContext.Provider>
  );
};

export const useProjektRechte = (): ProjektRechteContextType => {
  const context = useContext(ProjektRechteContext);
  if (!context) {
    throw new Error(
      "useProjektRechte muss innerhalb eines ProjektRechteProvider verwendet werden",
    );
  }
  return context;
};
